import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ButtonTypeEnum } from '../../../enums/button-type-enum';
import {
  LocationEnum,
  PositionEnum,
  SalaryPeriodEnum,
} from '../../../enums/job-post-enum';
import { SnackbarSeverityEnum } from '../../../enums/snackbar-severity-enum';
import { handleApiResponseErrors } from '../../../errors/rtk-query-errors/rtk-query.errors';
import useSnackbar from '../../../hooks/useSnackBar';
import {
  ICreateJobpostRequest,
  IJopPostInputs,
  TLocation,
  TPosition,
  TSalaryPeriod,
} from '../../../interfaces/jobpost.interface';
import { getIndustryIds } from '../../../utils/jobpost';
import JobPostFilterMultiSelect from '../../../pages/JobPostsPage/components/JobPostsSidebar/JobPostsFilters/JobPostFilterMultiSelect';
import JobPostFilterSingleSelect from '../../../pages/JobPostsPage/components/JobPostsSidebar/JobPostsFilters/JobPostFilterSingleSelect';
import {
  useCreateJobpostMutation,
  useGetIndustriesQuery,
  useGetJobpostByIdQuery,
  useMoveToDraftMyJobpostByIdMutation,
  useUpdateJobpostMutation,
} from '../../../store/apiSlice/jobpost/jobpostApi';
import { colors } from '../../../theme/theme';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import createArrayFromEnum from '../../../utils/createArrayFromEnum';
import { errorHelper } from '../../../utils/helper/error-helper';
import LeftArrowIcon from '../../Icons/LeftArrowIcon';
import CustomButton from '../../MUIComponents/CustomButton';
import Input from '../../MUIComponents/Input';
import SnackbarCustom from '../../MUIComponents/SnackbarCustom';
import TipTapEditor from '../../TipTapEditor';

const AddNewJobPostForm = () => {
  const { id } = useParams();
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const { data: jobPostData, isLoading: getJobPostLoading } =
    useGetJobpostByIdQuery(id);

  const {
    control,
    register,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<IJopPostInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (id !== 'new' && jobPostData?.jobpost) {
      const { jobpost: jobPost } = jobPostData;

      (async () => {
        setValue('title', jobPost.title);
        setValue('description', jobPost.description);
        setValue('minSalary', jobPost.minSalary);
        setValue('maxSalary', jobPost.maxSalary);
        setValue('position', jobPost.position);
        setValue('location', jobPost.location);
        setValue('salaryPeriod', jobPost.salaryPeriod);
        setValue('industry', getIndustryIds(jobPost));

        await trigger();
      })();
    }
  }, [id, jobPostData, setValue, trigger]);

  const [createJobpost, { isLoading: isCreateJobpostLoading }] =
    useCreateJobpostMutation();
  const [updateJobpost, { isLoading: isUpdateLoading }] =
    useUpdateJobpostMutation();

  const [moveJobpostToDraft] = useMoveToDraftMyJobpostByIdMutation();

  const postJob: SubmitHandler<IJopPostInputs> = async (data) => {
    if (!isFormValid()) return;

    const jobPost: ICreateJobpostRequest = {
      title: data.title,
      industryIds: data.industry,
      position: data.position,
      location: data.location,
      description: data.description,
      maxSalary: Number(data.maxSalary),
      minSalary: Number(data.minSalary),
      salaryPeriod: data.salaryPeriod,
      isDraft: false,
    };

    try {
      if (id === 'new') {
        handleApiResponseErrors(await createJobpost(jobPost));
      } else {
        handleApiResponseErrors(await moveJobpostToDraft(id as string));
        handleApiResponseErrors(
          await updateJobpost({
            jobpostId: id,
            data: { ...jobPost, isDraft: false },
          })
        );
      }

      showSnackbar('Job posted successfully!', SnackbarSeverityEnum.SUCCESS);
      setTimeout(() => {
        reset();
        navigate('/?tab=1');
      }, 1500);
    } catch (error) {
      showSnackbar(`Error: ${errorHelper(error)}`, SnackbarSeverityEnum.ERROR);
    }
  };

  const saveDraft: SubmitHandler<IJopPostInputs> = async (data) => {
    if (!isFormValid()) return;
    const jobPostDraft: ICreateJobpostRequest = {
      title: data.title,
      industryIds: data.industry,
      position: data.position,
      location: data.location,
      description: data.description,
      maxSalary: Number(data.maxSalary),
      minSalary: Number(data.minSalary),
      salaryPeriod: data.salaryPeriod,
      isDraft: true,
    };
    try {
      if (id === 'new') {
        handleApiResponseErrors(await createJobpost(jobPostDraft));
      } else {
        handleApiResponseErrors(await moveJobpostToDraft(id as string));
        handleApiResponseErrors(
          await updateJobpost({
            jobpostId: id,
            data: { ...jobPostDraft, isDraft: true },
          })
        );
      }

      showSnackbar('Draft saved successfully!', SnackbarSeverityEnum.INFO);
      setTimeout(() => {
        reset();
        navigate('/?tab=1');
      }, 1500);
    } catch (error) {
      showSnackbar(`Error: ${errorHelper(error)}`, SnackbarSeverityEnum.ERROR);
    }
  };

  const { data: sportsIndustries } = useGetIndustriesQuery();

  const locationsArray: TLocation[] = createArrayFromEnum(
    LocationEnum,
    'location'
  );

  const salaryPeriodsArray: TSalaryPeriod[] = createArrayFromEnum(
    SalaryPeriodEnum,
    'salaryPeriod'
  );

  const positionsArray: TPosition[] = createArrayFromEnum(
    PositionEnum,
    'position'
  );

  const isFormValid = () => {
    const minSalary = Number(watch('minSalary'));
    const maxSalary = Number(watch('maxSalary'));
    const description = watch('description');

    if (maxSalary !== 0 && minSalary > maxSalary) {
      showSnackbar(
        'Min salary cannot be greater than max salary.',
        SnackbarSeverityEnum.ERROR
      );
      return false;
    }

    if (minSalary < 0 || maxSalary < 0) {
      showSnackbar(
        'Salaries must be greater than or equal to 0.',
        SnackbarSeverityEnum.ERROR
      );
      return false;
    }

    if (!description) {
      showSnackbar('Description is required.', SnackbarSeverityEnum.ERROR);
      return false;
    }

    if (errors.title || errors.minSalary || errors.maxSalary) {
      showSnackbar(
        'Please fill all required fields: industry, position, location, salary period, and ensure all input fields are valid.',
        SnackbarSeverityEnum.ERROR
      );
      return false;
    }

    return true;
  };

  const [isSubmitDisabled, setSubmitDisabled] = useState(true);

  useEffect(() => {
    const minSalary = Number(watch('minSalary'));
    const maxSalary = Number(watch('maxSalary'));
    const description = watch('description');

    setSubmitDisabled(
      !isValid ||
        minSalary < 0 ||
        maxSalary < 0 ||
        !description ||
        isCreateJobpostLoading ||
        isUpdateLoading
    );
  }, [isValid, isCreateJobpostLoading, isUpdateLoading, watch]);

  const validateDescriptionMinLength = (value: string) => {
    const stripHTMLTags = (str: string) => str.replace(/<[^>]*>/g, '');

    if (stripHTMLTags(value).length < 200) {
      return 'The description must be at least 200 characters long.';
    }
    return true;
  };

  return (
    <Box
      display={'flex'}
      maxWidth={'875px'}
      width={'100%'}
      margin={'0 auto'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      marginBottom={'200px'}
    >
      <Box
        width={'100%'}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
      >
        <Box
          width={'100%'}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          bgcolor={'background.white'}
        >
          <Box
            width={'100%'}
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            borderBottom={'1px solid'}
            borderColor={'background.darker'}
            padding={{
              xs: '22px 16px',
              lg: '36px 22px',
            }}
          >
            <Box
              display={'flex'}
              flexDirection={'row'}
              gap={'16px'}
              alignItems={'center'}
              onClick={() => navigate('/?tab=1&page=1')}
              sx={{ cursor: 'pointer' }}
            >
              <IconButton
                aria-label="More"
                sx={{
                  p: 0,
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  minHeight: '24px',
                  '&:hover': { background: 'transparent' },
                }}
                aria-haspopup="true"
              >
                <LeftArrowIcon color={colors.lightGreyBg} />
              </IconButton>
              <Typography variant="h3" component="h3">
                Go back
              </Typography>
            </Box>
          </Box>

          <Box
            width={'100%'}
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
          >
            <Box
              width={'100%'}
              display={'flex'}
              flexDirection={'column'}
              padding={{
                xs: '28px 16px',
                lg: '36px',
              }}
              gap={'36px'}
            >
              <Typography component="h2" variant="h2">
                New job post
              </Typography>

              <Box
                width={'100%'}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'center'}
                gap={mobileView ? '2.25rem' : '3rem'}
              >
                <Box display="flex">
                  <Input
                    caption="Job Title:"
                    containerFlex={
                      mobileView ? '0 1 100%' : '0 1 calc(50% - 8px)'
                    }
                    variant="outlined"
                    fullWidth
                    placeholder="Enter job title"
                    register={register('title', {
                      required: 'Job title is required',
                      minLength: {
                        value: 3,
                        message: 'Job title must be at least 3 characters',
                      },
                    })}
                    error={!!errors.title}
                    helperText={errors.title ? errors.title.message : ''}
                  />
                </Box>
                <Box
                  display="flex"
                  sx={(theme) => ({
                    width: `calc(50% - 8px)`,
                    [theme.breakpoints.down('md')]: {
                      width: '100%',
                    },
                  })}
                >
                  <Box width={'100%'}>
                    <JobPostFilterMultiSelect
                      name={'industry'}
                      control={control}
                      validationRules={{
                        required: 'Industry is required',
                      }}
                      label={'industry'}
                      placeholder={'Choose one'}
                      selectItems={sportsIndustries || []}
                      helperText={errors.industry?.message || ''}
                      error={!!errors.industry}
                    />
                  </Box>
                </Box>
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    gap: '16px',
                    [theme.breakpoints.down('md')]: {
                      flexDirection: 'column',
                      gap: '36px',
                    },
                  })}
                >
                  <Box width={'100%'}>
                    <JobPostFilterSingleSelect
                      name={'position'}
                      control={control}
                      validationRules={{
                        required: 'Employment Type is required',
                      }}
                      label={'position'}
                      placeholder={'Choose one'}
                      selectItems={positionsArray}
                      helperText={errors.position?.message || ''}
                      error={!!errors.position}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <JobPostFilterSingleSelect
                      name={'location'}
                      control={control}
                      validationRules={{
                        required: 'Work Type is required',
                      }}
                      label={'location'}
                      placeholder={'Choose one'}
                      selectItems={locationsArray}
                      helperText={errors.location?.message || ''}
                    />
                  </Box>
                </Box>

                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: 'Description is required',
                    validate: validateDescriptionMinLength,
                  }}
                  render={({ field }) => (
                    <TipTapEditor
                      label="Description:"
                      value={field.value || ''}
                      onChange={field.onChange}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      maxLength={8000}
                      showRemainingChars
                    />
                  )}
                />

                <Box display="flex" gap="16px">
                  <Input
                    caption="Min Salary:"
                    containerFlex="1"
                    variant="outlined"
                    fullWidth
                    type="number"
                    placeholder="Min Salary"
                    register={register('minSalary', {
                      // required: 'Minimum salary is required',
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Minimum salary must be a valid number',
                      },
                      max: {
                        value: 9000000,
                        message: 'Maximum salary cannot exceed 9,000,000',
                      },
                    })}
                    error={!!errors.minSalary}
                    helperText={
                      errors.minSalary ? errors.minSalary.message : ''
                    }
                  />
                  <Input
                    caption="Max Salary:"
                    containerFlex="1"
                    variant="outlined"
                    fullWidth
                    type="number"
                    placeholder="Max Salary"
                    register={register('maxSalary', {
                      // required: 'Maximum salary is required',
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Maximum salary must be a valid number',
                      },
                      max: {
                        value: 9000000,
                        message: 'Maximum salary cannot exceed 9,000,000',
                      },
                    })}
                    error={!!errors.maxSalary}
                    helperText={
                      errors.maxSalary ? errors.maxSalary.message : ''
                    }
                  />
                </Box>

                <JobPostFilterSingleSelect
                  name={'salaryPeriod'}
                  control={control}
                  validationRules={{
                    required: 'Salary Period is required',
                  }}
                  label={'salaryPeriod'}
                  placeholder={'Choose salary period'}
                  selectItems={salaryPeriodsArray}
                  helperText={errors.salaryPeriod?.message || ''}
                />
              </Box>
            </Box>

            <Box
              width={'100%'}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              borderTop={'1px solid'}
              borderColor={'background.darker'}
              padding={{
                xs: '16px',
                lg: '36px',
              }}
            >
              <Box display="flex" flexDirection={'row'} gap="16px">
                <CustomButton
                  buttonWidth={mobileView ? '100%' : '220px'}
                  variantType={ButtonTypeEnum.PRIMARY}
                  onClick={handleSubmit(postJob)}
                  disabled={isSubmitDisabled}
                >
                  {isCreateJobpostLoading ||
                  getJobPostLoading ||
                  isUpdateLoading
                    ? 'Posting...'
                    : 'Post job'}
                </CustomButton>
                <CustomButton
                  buttonWidth={mobileView ? '100%' : '220px'}
                  variantType={ButtonTypeEnum.SECONDARY}
                  sx={{ borderColor: 'background.dark' }}
                  onClick={handleSubmit(saveDraft)}
                  disabled={isSubmitDisabled}
                >
                  {isCreateJobpostLoading || isUpdateLoading
                    ? 'Saving...'
                    : 'Save draft'}
                </CustomButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <SnackbarCustom
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default AddNewJobPostForm;
